import {
  Component,
  OnInit,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef,
  input
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

import { GuidebookService, GuidebookConfig } from 'src/shared/services/guidebook.service';

import {
  GuidebookUpsellConfig,
  GuidebookUpsellType,
  GuidebookUpsellPriceType,
  BUILT_IN_UPSELLS,
  COMMON_CUSTOM_FEE_TEMPLATES
} from 'src/models/guidebook-upsell-config.model';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

@Component({
  selector: 'guidebook-upsell-config',
  templateUrl: './guidebook-upsell-config.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class GuidebookUpsellConfigComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private util = inject(Util);
  private guidebookService = inject(GuidebookService);

  readonly windowService = inject(WindowService);

  guidebookConfig: GuidebookConfig;
  upsellConfigs: GuidebookUpsellConfig[] = [];
  isDataLoaded = false;
  isRightSideContent = false;
  errorMessage: string;
  submitted = false;

  waivoDamageProtection: GuidebookUpsellConfig | null = null;
  viatorAffiliate: GuidebookUpsellConfig | null = null;
  customFees: GuidebookUpsellConfig[] = [];

  waivoEnabled = false;
  viatorEnabled = true;

  customFeeTemplates = COMMON_CUSTOM_FEE_TEMPLATES;
  showAddCustomFee = false;
  customFeeForm: FormGroup;

  priceTypeItems = [
    { viewValue: 'Fixed Amount', value: 'fixed' },
    { viewValue: 'Per Night', value: 'per_night' },
    { viewValue: 'Percentage of Stay', value: 'percentage' },
  ];

  boundSaveWaivoConfig = this.saveWaivoConfig.bind(this);
  boundSaveViatorConfig = this.saveViatorConfig.bind(this);
  boundAddCustomFee = this.addCustomFee.bind(this);

  async ngOnInit() {
    try {
      this.isRightSideContent = (await this.storage.retrieve('dialog-right-side-open')) || false;
      const params = await this.storage.retrieve('dialog-params');
      const guidebookConfigId = params?.guidebookConfigId;

      if (this.isRightSideContent) {
        this.rightSidePanelService.setDialogPageTitle('Guidebook Upsell Configuration');
      }

      this.customFeeForm = this.fb.group({
        name: ['', Validators.required],
        description: [''],
        price: [null, [Validators.required, Validators.min(0)]],
        price_type: ['fixed', Validators.required],
        is_optional: [true],
      });

      if (guidebookConfigId) {
        await this.loadPage(guidebookConfigId);
      }
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load upsell configuration';
      this.util.showError(this.errorMessage, 10000);
    }
  }

  async loadPage(guidebookConfigId: number) {
    try {
      this.guidebookConfig = await this.guidebookService.getGuidebookConfig(guidebookConfigId);
    } catch {
      this.errorMessage = 'Failed to load guidebook configuration';
      this.isDataLoaded = true;
      this.cdr.markForCheck();
      return;
    }

    try {
      this.upsellConfigs = await this.guidebookService.getUpsellConfigs(guidebookConfigId);
    } catch {
      this.upsellConfigs = [];
    }

    this.categorizeUpsells();
    this.isDataLoaded = true;
    this.cdr.markForCheck();
  }

  private categorizeUpsells(): void {
    this.waivoDamageProtection = this.upsellConfigs.find(c => c.type === 'waivo_damage_protection') || null;
    this.viatorAffiliate = this.upsellConfigs.find(c => c.type === 'viator_affiliate') || null;
    this.customFees = this.upsellConfigs.filter(c => c.type === 'custom_fee');

    this.waivoEnabled = this.waivoDamageProtection?.enabled || false;
    this.viatorEnabled = this.viatorAffiliate?.enabled ?? true;
  }

  async saveWaivoConfig() {
    try {
      const configData: Partial<GuidebookUpsellConfig> = {
        type: 'waivo_damage_protection',
        name: 'Waivo Damage Protection',
        description: BUILT_IN_UPSELLS[0].description,
        enabled: this.waivoEnabled,
        price: null,
        price_type: null,
        is_optional: true,
        settings: {},
      };

      if (this.waivoDamageProtection) {
        await this.guidebookService.updateUpsellConfig(
          this.guidebookConfig.id,
          this.waivoDamageProtection.id,
          configData
        );
      } else {
        await this.guidebookService.createUpsellConfig(this.guidebookConfig.id, configData);
      }
      this.util.showSuccess('Waivo damage protection settings saved', 3000);
      await this.loadPage(this.guidebookConfig.id);
    } catch (err) {
      this.util.showError('Failed to save Waivo settings', 3000);
    }
  }

  async saveViatorConfig() {
    try {
      const configData: Partial<GuidebookUpsellConfig> = {
        type: 'viator_affiliate',
        name: 'Viator Experiences',
        description: BUILT_IN_UPSELLS[1].description,
        enabled: this.viatorEnabled,
        price: null,
        price_type: null,
        is_optional: true,
        settings: {},
      };

      if (this.viatorAffiliate) {
        await this.guidebookService.updateUpsellConfig(
          this.guidebookConfig.id,
          this.viatorAffiliate.id,
          configData
        );
      } else {
        await this.guidebookService.createUpsellConfig(this.guidebookConfig.id, configData);
      }
      this.util.showSuccess('Viator affiliate settings saved', 3000);
      await this.loadPage(this.guidebookConfig.id);
    } catch (err) {
      this.util.showError('Failed to save Viator settings', 3000);
    }
  }

  async addCustomFee() {
    this.submitted = true;
    if (!this.customFeeForm.valid) {
      return;
    }

    try {
      const formValue = this.customFeeForm.value;
      const configData: Partial<GuidebookUpsellConfig> = {
        type: 'custom_fee',
        name: formValue.name,
        description: formValue.description || '',
        enabled: true,
        price: formValue.price,
        price_type: formValue.price_type,
        is_optional: formValue.is_optional,
        settings: {},
      };

      await this.guidebookService.createUpsellConfig(this.guidebookConfig.id, configData);
      this.util.showSuccess(`Custom fee "${formValue.name}" added`, 3000);
      this.customFeeForm.reset({ price_type: 'fixed', is_optional: true });
      this.submitted = false;
      this.showAddCustomFee = false;
      await this.loadPage(this.guidebookConfig.id);
    } catch (err) {
      this.util.showError('Failed to add custom fee', 3000);
    }
  }

  async toggleCustomFee(fee: GuidebookUpsellConfig) {
    try {
      await this.guidebookService.updateUpsellConfig(
        this.guidebookConfig.id,
        fee.id,
        { enabled: !fee.enabled }
      );
      await this.loadPage(this.guidebookConfig.id);
    } catch (err) {
      this.util.showError('Failed to update custom fee', 3000);
    }
  }

  async removeCustomFee(fee: GuidebookUpsellConfig) {
    try {
      await this.guidebookService.deleteUpsellConfig(this.guidebookConfig.id, fee.id);
      this.util.showSuccess(`Removed "${fee.name}"`, 3000);
      await this.loadPage(this.guidebookConfig.id);
    } catch (err) {
      this.util.showError('Failed to remove custom fee', 3000);
    }
  }

  prefillFromTemplate(template: { name: string; description: string; price_type: GuidebookUpsellPriceType }) {
    this.customFeeForm.patchValue({
      name: template.name,
      description: template.description,
      price_type: template.price_type,
    });
    this.showAddCustomFee = true;
    this.cdr.markForCheck();
  }

  toggleAddCustomFee() {
    this.showAddCustomFee = !this.showAddCustomFee;
    if (!this.showAddCustomFee) {
      this.customFeeForm.reset({ price_type: 'fixed', is_optional: true });
      this.submitted = false;
    }
    this.cdr.markForCheck();
  }

  formatPrice(fee: GuidebookUpsellConfig): string {
    if (fee.price === null || fee.price === undefined) {
      return 'N/A';
    }
    if (fee.price_type === 'percentage') {
      return `${fee.price}%`;
    }
    if (fee.price_type === 'per_night') {
      return `$${fee.price}/night`;
    }
    return `$${fee.price}`;
  }

  goBack() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateBack('guidebook');
    }
  }
}
